import { useState } from 'react';
import {
  PET_TYPES,
  CAT_BREEDS,
  DOG_BREEDS,
  OTHER_SPECIES,
} from 'accounts/constants/home-pets';
import { HOME_PETS_STRINGS } from 'accounts/constants/strings/home-pets.strings';
import { uniqBy } from 'es-toolkit/compat';

export function useHomePets({
  form,
  initialValues,
}: {
  form: any;
  initialValues: any;
}) {
  // used to get the initial unique breeds to display and set the breed tags to show.
  // This is different than seletectedBreeds which is using a
  // controlled component that only accepts strings in downshift.
  const startingRestrictedBreedsArray =
    initialValues.restrictedBreeds?.map((value: string) => ({
      value,
      key: value,
    })) || [];

  const AllCats = structuredClone(CAT_BREEDS);
  const AllDogs = structuredClone(DOG_BREEDS);
  const OtherSpecies = structuredClone(OTHER_SPECIES);
  const allPetsToUnique = [...AllDogs, ...AllCats, ...OtherSpecies];
  const allPetsUnique = uniqBy(allPetsToUnique, 'value');
  let initialPetTypeStrings = HOME_PETS_STRINGS.petTypes.ALL;
  let uniqueBreeds = uniqBy(
    [
      ...startingRestrictedBreedsArray,
      ...DOG_BREEDS,
      ...CAT_BREEDS,
      ...OTHER_SPECIES,
    ],
    'value',
  );

  if (initialValues?.petsAllowed === PET_TYPES.CATS) {
    initialPetTypeStrings = HOME_PETS_STRINGS.petTypes.CATS;
    uniqueBreeds = uniqBy(
      [...startingRestrictedBreedsArray, ...CAT_BREEDS],
      'value',
    );
  }

  if (initialValues?.petsAllowed === PET_TYPES.DOGS) {
    initialPetTypeStrings = HOME_PETS_STRINGS.petTypes.DOGS;
    uniqueBreeds = uniqBy(
      [...startingRestrictedBreedsArray, ...DOG_BREEDS],
      'value',
    );
  }

  const [breedOptions, setBreed] = useState(uniqueBreeds);
  const [hideGreenInfoBox, setHideGreenInfoBox] = useState(false);
  const [hideAnimationGreenInfoBox, setHideAnimateGreenInfoBox] =
    useState(false);
  const [petTypeStrings, setPetTypeStrings] = useState(initialPetTypeStrings);
  const [selectedBreeds, setSelectedBreeds] = useState(
    initialValues.restrictedBreeds || [],
  );
  const [breedTagsToShow, setBreedTagsToShow] = useState(
    startingRestrictedBreedsArray,
  );
  const [inputValue, setInputValue] = useState('');

  // TODO: refactor this into MultiSelectComponent
  function handleChangeInput(inputVal: string) {
    const t = inputVal.split(',');

    if (JSON.stringify(t) !== JSON.stringify(selectedBreeds)) {
      setInputValue(inputVal);
    }
  }

  // TODO: refactor this into MultiSelectComponent
  function handleRemoveTag(item: any, input: any = {}) {
    let itemToRemove;
    const removeItems = breedTagsToShow.filter((i: any) => {
      if (i.value === item) {
        itemToRemove = i;
        return false;
      }

      return item;
    });

    const newSelectedItem = selectedBreeds.filter((i: any) => item !== i);

    // This is to sort adding back items to the array. Otherwise they are put to the bottom of the array.
    const alphabetizeArrayToSet = uniqBy(
      [...breedOptions, itemToRemove],
      'value',
    ).sort((a, b) => {
      if (a.value > b.value) {
        return 1;
      }

      if (a.value < b.value) {
        return -1;
      }

      return 0;
    });

    setBreed(alphabetizeArrayToSet);
    setBreedTagsToShow(removeItems);
    setSelectedBreeds(newSelectedItem);
    setInputValue('');

    if (input.onChange) {
      input.onChange(newSelectedItem);
    }
  }

  // TODO: refactor this into MultiSelectComponent
  function addSelectedItem(item: any, input: any = {}) {
    let itemToAdd;
    const newItems = breedOptions.filter((i: any) => {
      if (i.value === item) {
        itemToAdd = i;
        return false;
      }

      return i;
    });

    // `item` is whichever option the menu offered, and the menu is rendered from
    // `breedOptions` — the very list being filtered here — so the match always
    // lands. Without one this already threw on the next line.
    const addedItem = itemToAdd!;

    setSelectedBreeds([...selectedBreeds, addedItem.value]);
    setBreedTagsToShow([...breedTagsToShow, addedItem]);
    setBreed(newItems);
    setInputValue('');

    if (input.onChange) {
      input.onChange([...selectedBreeds, addedItem.value]);
    }
  }

  // TODO: refactor this into MultiSelectComponent
  const handleChange = (selectedItem: any, input: any) => {
    if (selectedBreeds.includes(selectedItem)) {
      handleRemoveTag(selectedItem, input);
    } else {
      addSelectedItem(selectedItem, input);
    }
  };

  function closeGreenBoxMenu() {
    setHideAnimateGreenInfoBox(true);
  }

  function changeFormValue(value: any) {
    const { restrictedBreeds, hasWeightRestriction, hasRestrictedBreeds } =
      value;

    form.batch(() => {
      form.change(hasWeightRestriction, false);
      form.change(hasRestrictedBreeds, false);
      form.change(restrictedBreeds, []);
    });
  }

  function handleChangePetsAllowed(e: any) {
    if (e === PET_TYPES.ALL) {
      setBreedTagsToShow([]);
      setSelectedBreeds([]);
      setBreed(allPetsUnique);
      setPetTypeStrings(HOME_PETS_STRINGS.petTypes.ALL);
      changeFormValue({
        restrictedBreeds: 'restrictedBreeds',
        hasWeightRestriction: 'hasWeightRestriction',
        hasRestrictedBreeds: 'hasRestrictedBreeds',
      });
    } else if (e === PET_TYPES.DOGS) {
      setBreedTagsToShow([]);
      setSelectedBreeds([]);
      setBreed(AllDogs);
      setPetTypeStrings(HOME_PETS_STRINGS.petTypes.DOGS);
      changeFormValue({
        restrictedBreeds: 'restrictedBreeds',
        hasWeightRestriction: 'hasWeightRestriction',
        hasRestrictedBreeds: 'hasRestrictedBreeds',
      });
    } else if (e === PET_TYPES.CATS) {
      setBreed(AllCats);
      setSelectedBreeds([]);
      setBreedTagsToShow([]);
      setPetTypeStrings(HOME_PETS_STRINGS.petTypes.CATS);
      changeFormValue({
        restrictedBreeds: 'restrictedBreeds',
        hasWeightRestriction: 'hasWeightRestriction',
        hasRestrictedBreeds: 'hasRestrictedBreeds',
      });
    } else if (e === PET_TYPES.NONE) {
      setBreed([]);
    }
  }

  return {
    inputValue,
    petTypeStrings,
    breedOptions,
    selectedBreeds,
    breedTagsToShow,
    hideGreenInfoBox,
    hideAnimationGreenInfoBox,
    handleRemoveTag,
    setHideAnimateGreenInfoBox,
    setHideGreenInfoBox,
    handleChangeInput,
    handleChange,
    closeGreenBoxMenu,
    changeFormValue,
    handleChangePetsAllowed,
  };
}
